import type { UnknownLabel } from "@sitescene/types";

import { useState } from "react";

import { type Column, DataTable } from "@/components/data-table";
import { Button, Field, Select, TextInput } from "@/components/field";
import { StatusMessage } from "@/components/status-message";
import { useTaxonomy } from "@/features/plausibility/use-taxonomy";
import {
	useAddAlias,
	useIgnoreUnknownLabels,
	useUnknownLabels,
} from "@/features/plausibility/use-unknown-labels";

import { formatWhen } from "./format";

/**
 * What the taxonomy is missing. Each row is a distinct Stage 1 label the
 * alias table and the classifier both failed to place, counted across every
 * organization. Mapping one adds an alias so the next sighting verifies;
 * ignoring one records that someone looked and decided it needs no class.
 */
export const UnknownLabels = () => {
	const labels = useUnknownLabels("pending");
	const taxonomy = useTaxonomy();
	const addAlias = useAddAlias();
	const ignore = useIgnoreUnknownLabels();
	const [drafts, setDrafts] = useState<
		Record<string, { alias: string; classId: string }>
	>({});

	const rows = labels.data ?? [];
	const classes = taxonomy.data ?? [];

	// A detection row's `rawType` is the label itself, so it makes a sensible
	// starting alias. A reviewer row's is the `reviewer-note` sentinel and its
	// sample is a whole sentence — neither belongs in the global alias table, so
	// staff type the phrase themselves.
	const draftFor = (row: UnknownLabel) =>
		drafts[row.id] ?? {
			alias: row.source === "reviewer" ? "" : row.rawType,
			classId: "",
		};
	const setDraft = (
		id: string,
		patch: Partial<{ alias: string; classId: string }>,
	) =>
		setDrafts((d) => ({
			...d,
			[id]: { ...(d[id] ?? { alias: "", classId: "" }), ...patch },
		}));

	const columns: Column<UnknownLabel>[] = [
		{
			header: "Type",
			key: "type",
			render: (row) => <code className="text-xs">{row.rawType}</code>,
		},
		{
			// A reviewer's sentence and an analyzer's label are both taxonomy gaps
			// but read nothing alike, so say which this is before staff try to
			// turn a whole sentence into an alias.
			header: "From",
			key: "source",
			render: (row) => (row.source === "reviewer" ? "reviewer" : "detection"),
		},
		{
			header: "Labels",
			key: "labels",
			render: (row) => row.rawLabels.join(", ") || "—",
		},
		{
			header: "Seen",
			key: "seen",
			render: (row) => (
				<span>
					{row.count}× · last {formatWhen(row.lastSeenAt)}
				</span>
			),
		},
		{
			header: "Sample",
			key: "sample",
			render: (row) => (
				<span className="text-secondary text-xs">
					{row.descriptionSample ?? "—"}
				</span>
			),
		},
		{
			header: "Map to",
			key: "map",
			render: (row) => {
				const draft = draftFor(row);
				const busy = addAlias.isPending || ignore.isPending;
				return (
					<form
						className="flex flex-wrap items-end gap-2"
						onSubmit={(event) => {
							event.preventDefault();
							if (!draft.classId || !draft.alias.trim()) return;
							addAlias.mutate({
								alias: draft.alias.trim(),
								classId: draft.classId,
								unknownLabelIds: [row.id],
							});
						}}
					>
						<Field htmlFor={`class-${row.id}`} label="Class">
							<Select
								id={`class-${row.id}`}
								onChange={(e) => setDraft(row.id, { classId: e.target.value })}
								value={draft.classId}
							>
								<option value="">Choose…</option>
								{classes.map((cls) => (
									<option key={cls.classId} value={cls.classId}>
										{cls.classId}
									</option>
								))}
							</Select>
						</Field>
						<Field htmlFor={`alias-${row.id}`} label="Alias">
							<TextInput
								id={`alias-${row.id}`}
								onChange={(e) => setDraft(row.id, { alias: e.target.value })}
								placeholder={
									row.source === "reviewer" ? "phrase to map" : undefined
								}
								value={draft.alias}
							/>
						</Field>
						<Button
							disabled={busy || !draft.classId || !draft.alias.trim()}
							type="submit"
						>
							Add alias
						</Button>
						<Button
							disabled={busy}
							onClick={() => ignore.mutate([row.id])}
							type="button"
							variant="danger"
						>
							Ignore
						</Button>
					</form>
				);
			},
		},
	];

	return (
		<div>
			<p className="text-secondary text-sm">
				Labels the verifier could not place in the taxonomy. Adding an alias
				bumps the taxonomy version; the next detection with that label verifies.
			</p>
			{(addAlias.error || ignore.error) && (
				<p className="text-error-primary mt-2 text-sm" role="alert">
					{(addAlias.error ?? ignore.error)?.message}
				</p>
			)}
			<div className="mt-4">
				<StatusMessage
					empty="No unknown labels. Every recent detection mapped to a class."
					error={labels.error ?? taxonomy.error}
					isEmpty={!labels.isLoading && rows.length === 0}
					isLoading={labels.isLoading || taxonomy.isLoading}
					loading="Loading unknown labels…"
				/>
				{rows.length > 0 && (
					<DataTable columns={columns} rowKey={(row) => row.id} rows={rows} />
				)}
			</div>
		</div>
	);
};
